"use client";

import React, { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
function AnimatedCounter({
  value,
  isVisible
}: {
  value: number;
  isVisible: boolean;
}) {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (!isVisible || hasAnimated.current) return;
    hasAnimated.current = true;
    let start = 0;
    const duration = 2000;
    const fps = 60;
    const increment = value / (duration / (1000 / fps));
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / fps);
    return () => clearInterval(timer);
  }, [value, isVisible]);
  return <>{count}</>;
}
const metrics = [{
  value: 500,
  suffix: "+",
  label: "ΑΣΦΑΛΕΙΣ ΚΟΜΒΟΙ"
}, {
  value: 24,
  suffix: "/7",
  label: "ΠΑΡΑΚΟΛΟΥΘΗΣΗ"
}, {
  value: 99,
  suffix: ".9%",
  label: "UPTIME ΣΥΣΤΗΜΑΤΩΝ"
}, {
  value: 150,
  suffix: "+",
  label: "ΕΝΕΡΓΟΙ ΠΕΛΑΤΕΣ"
}];
export default function DsServicesInNumbers() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <section id="η-ds-services-σε-αριθμούς" className="dark bg-background text-foreground relative py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-primary)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-[0.03] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] max-w-4xl h-[200px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={`transition-all duration-1000 ease-out transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="flex flex-col md:flex-row items-center justify-between bg-card/40 backdrop-blur-xl border border-primary/30 rounded-2xl p-8 md:p-12 shadow-[0_0_40px_rgba(0,229,255,0.05),inset_0_0_20px_rgba(0,229,255,0.02)]">
            {metrics.map((metric, i) => <div data-index={i} key={i} className="flex flex-col md:flex-row items-center flex-1 w-full md:w-auto">
                {}
                <div className="flex flex-col items-center justify-center text-center w-full py-6 md:py-0">
                  <div className="text-5xl md:text-6xl font-bold text-primary drop-shadow-[0_0_15px_rgba(0,229,255,0.6)] flex items-baseline tracking-tighter">
                    <AnimatedCounter value={metric.value} isVisible={isVisible} />
                    <span className="text-4xl md:text-5xl ml-1">{metric.suffix}</span>
                  </div>
                  <div className="font-mono text-sm md:text-base text-muted-foreground mt-4 uppercase tracking-widest">
                    {metric.label}
                  </div>
                </div>

                {}
                {i < metrics.length - 1 && <>
                    <Separator orientation="vertical" className="hidden md:block h-24 mx-4 lg:mx-8 bg-primary/40 shadow-[0_0_12px_var(--color-primary)]" />
                    <Separator orientation="horizontal" className="block md:hidden w-24 mx-auto bg-primary/40 shadow-[0_0_12px_var(--color-primary)]" />
                  </>}
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}